<template>
  <Teleport v-if="visible" :to="`#${UI_POPUP_ROOT_ID}`">
    <div
      ref="balloonRef"
      class="field-verify-error-overlay"
      :class="`field-verify-error-overlay--${box.placement}`"
      :style="overlayStyle"
      role="alert"
    >
      <div v-for="(message, index) in normalized" :key="index" class="field-verify-error-overlay__msg">
        {{ message }}
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { UI_POPUP_ROOT_ID } from '@/common/utils/uiScale'
import {
  placeFieldVerifyErrorOverlay,
  type FieldVerifyErrorBox,
} from '@/common/utils/placeFieldVerifyErrorOverlay'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  messages?: string | string[] | null
  anchorEl?: HTMLElement | null
}>()

const balloonRef = ref<HTMLElement | null>(null)
const box = ref<FieldVerifyErrorBox>({
  top: 0,
  left: 0,
  maxWidth: 0,
  placement: 'above',
})

const normalized = computed(() => {
  const raw = props.messages
  if (raw == null) return []
  const list = Array.isArray(raw) ? raw : [raw]
  return list.map((item) => String(item ?? '').trim()).filter((item) => item !== '')
})

const visible = computed(() => normalized.value.length > 0 && props.anchorEl != null)

const overlayStyle = computed(() => ({
  top: `${box.value.top}px`,
  left: `${box.value.left}px`,
  maxWidth: `${box.value.maxWidth}px`,
}))

const reposition = () => {
  const anchorEl = props.anchorEl
  if (!visible.value || !anchorEl) return
  const rect = anchorEl.getBoundingClientRect()
  const balloonHeight = balloonRef.value?.offsetHeight ?? 32
  box.value = placeFieldVerifyErrorOverlay({
    anchor: {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
    },
    balloonHeight,
    viewport: {
      width: typeof window !== 'undefined' ? window.innerWidth : 1920,
      height: typeof window !== 'undefined' ? window.innerHeight : 1080,
    },
  })
}

watch(
  [visible, () => props.anchorEl, normalized],
  async () => {
    await nextTick()
    reposition()
  },
  { immediate: true, flush: 'post' },
)

watch(balloonRef, () => {
  reposition()
})

onMounted(() => {
  window.addEventListener('resize', reposition)
  window.addEventListener('scroll', reposition, true)
  void nextTick().then(reposition)
})

onUnmounted(() => {
  window.removeEventListener('resize', reposition)
  window.removeEventListener('scroll', reposition, true)
})
</script>

<style lang="scss">
.field-verify-error-overlay {
  position: fixed;
  z-index: 5000;
  box-sizing: border-box;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-size: 0.75rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  pointer-events: none;
}

.field-verify-error-overlay__msg + .field-verify-error-overlay__msg {
  margin-top: 0.125rem;
}
</style>
